import {
  cva,
  type VariantProps,
} from 'class-variance-authority'
import Image from 'next/image'
import Link from 'next/link'

import CompetitionCover from '@/app/(main)/competition/images/competition-cover.png'
import CompetitionStatusView from '@/components/competition-status-view'
import ArrowRight from '@/components/icons/arrow-right.svg'
import {
  CompetitionStatus,
  getCompetitionStatus,
} from '@/constants/competition-status'
import {
  formatNumber,
  formatUTCTime,
} from '@/lib/formatter'
import { cn } from '@/lib/utils'
import { Competition } from '@/redux/competition'

const variants = cva(
  'rounded-3xl overflow-hidden relative', {
    variants: {
      size: {
        default: 'w-[24rem] h-[17.5rem] text-xs',
        lg: 'w-[39rem] h-[25rem]',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
)

export interface CompetitionCardProps extends VariantProps<typeof variants> {
  className?: string
  competition: Competition
  showStatus?: boolean
  showParticipants?: boolean
}

export default function CompetitionCard({
  size = 'default',
  showStatus,
  showParticipants,
  className,
  competition: {
    id,
    title,
    subtitle,
    applicantCount,
    applyStartAt,
    competitionEndAt,
  },
  competition,
}: CompetitionCardProps) {
  return (
    <Link
      href={`/competition/${id}`}
      className={cn(variants({ size, className }), 'border-1 border-white/15')}
    >
      {showParticipants && (
        <div
          className="absolute z-1 top-4 right-4 px-2 py-1 rounded-full border border-white/12 bg-black/50 backdrop-blur-xs font-semibold text-tiny"
        >
          Participants: {formatNumber()(applicantCount)}
        </div>
      )}
      <Image src={CompetitionCover} alt={title} fill />
      <div
        className={cn(
          'absolute bottom-[-1px] w-full bg-black/30 backdrop-blur-xl',
          size === 'default' && 'p-4',
          size === 'lg' && 'py-6 px-4',
        )}
      >
        <div className="flex gap-4 items-center justify-between mb-1">
          <div
            className={cn(
              'font-semibold truncate',
              size === 'default' && 'text-xl',
              size === 'lg' && 'text-2xl',
            )}
          >
            {title}
          </div>
          <ArrowRight
            className={cn(
              'shrink-0 p-1 fill-white rounded-full bg-primary',
              size === 'default' && 'size-6',
              size === 'lg' && 'size-8',
            )}
          />
        </div>
        <div className="truncate">{subtitle}</div>
        {showStatus && (
          <div className="flex items-center gap-2 mt-1">
            <CompetitionStatusView competition={competition} />
            <div>
              {getCompetitionStatus(competition) === CompetitionStatus.Inactive ? (
                <>
                  <span className="text-secondary-foreground">Apply Date:&nbsp;</span>
                  {formatUTCTime('MMMM DD, YYYY')(applyStartAt)} (UTC)
                </>
              ) : (
                <>
                  <span className="text-secondary-foreground">Ending Date:&nbsp;</span>
                  {formatUTCTime('MMMM DD, YYYY')(competitionEndAt)} (UTC)
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </Link>
  )
}
